// Devoluciones de venta (Notas de Crédito). POS → Venta / Caja → Devoluciones (NC).
// · Bloquea CONSUMIDOR FINAL (el SRI exige comprador identificado en la NC).
// · Parcial/total con control de saldo por línea (varias NC por factura).
// · Reembolso: efectivo (egreso de la sesión), monedero del cliente, o A LA
//   TARJETA (897) cuando la venta se cobró por la pasarela: se emite la NC y se
//   reversa el cobro, sin que la caja se mueva. Sólo en devoluciones TOTALES —
//   el reverso de PagoMedios no admite monto parcial.
// · Imprime según el formato del local (rollo 80mm / A4).
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Printer, RotateCcw, Search, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { useQuery as useRQ } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Caja, Campo, ClienteOpt, clientesList, fieldCls, money, pos, posRpc, fechaLocal, useImpresionConfig } from './shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { imprimirNotaCredito, type EmpresaTicket, type NotaCreditoImprimible } from './print';
import { useAuth } from '@/lib/hooks';
import { LOCALE } from '@/lib/utils/regional';

interface LineaVenta {
  venta_linea_id: string; producto_id: string | null; descripcion: string;
  cantidad: number; precio_unitario: number; descuento: number; iva_tarifa: number;
  subtotal: number; devuelto: number;
}
interface VentaDev {
  venta_id: string; codigo: string; fecha: string; total: number; caja: string;
  cliente_partner_id: string; cliente: string; identificacion: string | null;
  es_consumidor_final: boolean; factura: string | null; factura_fecha: string | null;
  factura_autorizada: boolean; devuelto_total: number; lineas: LineaVenta[];
}
interface DevRow {
  id: string; codigo: string; fecha: string; caja: string; cajero: string | null;
  venta: string; factura: string | null; nc_numero: string | null; nc_clave_acceso: string | null; nc_estado: string | null;
  cliente: string; identificacion: string | null; motivo: string; reembolso: string;
  subtotal: number; iva: number; total: number;
  lineas: { descripcion: string; cantidad: number; precio_unitario: number; descuento: number; subtotal: number; reingresa_stock: boolean }[];
}

interface VentaDevolvible { venta_id: string; codigo: string; fecha: string; total: number; cliente: string; identificacion: string | null; factura: string | null; devuelto: number }

const hoy = () => fechaLocal();

export function Devoluciones() {
  useImpresionConfig();  // deja lista la config del ticket (CFG) antes de imprimir la NC
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { user } = useAuth();
  const [cajaId, setCajaId] = useState('');
  const [q, setQ] = useState('');
  const [venta, setVenta] = useState<VentaDev | null>(null);
  const [cant, setCant] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState('');
  const [reembolso, setReembolso] = useState<'efectivo' | 'monedero' | 'tarjeta'>('efectivo');

  const [clienteId, setClienteId] = useState('');
  const { data: clientes = [] } = useQuery<ClienteOpt[]>({ queryKey: ['pos-clientes-dev'], queryFn: clientesList });
  // Últimas facturas con saldo devolvible (todas o del cliente elegido) — clic = cargar
  const { data: recientes = [], isFetching: cargandoRecientes } = useQuery<VentaDevolvible[]>({
    queryKey: ['pos-ventas-devolvibles', clienteId],
    queryFn: () => posRpc<VentaDevolvible[]>('pos_ventas_devolvibles', { _cliente: clienteId || null, _limite: 12 }),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: cajas = [] } = useQuery<Caja[]>({
    queryKey: ['pos-cajas-dev'], queryFn: () => pos.list<Caja>('cajas', '?select=*&es_activo=eq.true&deleted_at=is.null&order=nombre'),
  });
  useEffect(() => { if (!cajaId && cajas.length) setCajaId(cajas[0].id); }, [cajas, cajaId]);
  const caja = cajas.find((c) => c.id === cajaId);

  // Formato de impresión del local de la caja + datos de empresa para el impreso
  const { data: formato = 'rollo80' } = useQuery<string>({
    queryKey: ['pos-formato-local', caja?.location_id ?? ''],
    enabled: !!caja?.location_id,
    queryFn: async () => (await apiClient.get<{ formato_impresion: string }[]>(
      `/rest/inventario.locations?id=eq.${caja!.location_id}&select=formato_impresion`)).data[0]?.formato_impresion ?? 'rollo80',
  });
  const { data: empresa } = useRQ<EmpresaTicket>({
    queryKey: ['pos-emp-dev'], enabled: !!user?.empresa?.id,
    queryFn: async () => (await apiClient.get<EmpresaTicket[]>(
      `/rest/autenticacion.empresas?id=eq.${user!.empresa.id}&select=nombre,ruc,direccion,telefono,moneda:moneda_funcional`)).data[0] ?? {},
  });
  // Plazo máximo de devolución (días) parametrizado por empresa (0 = sin límite).
  const { data: diasMax = 0 } = useRQ<number>({
    queryKey: ['pos-dev-dias-max'], enabled: !!user?.empresa?.id,
    queryFn: async () => (await apiClient.get<{ devolucion_dias_max: number }[]>(
      `/rest/autenticacion.empresas?id=eq.${user!.empresa.id}&select=devolucion_dias_max`)).data[0]?.devolucion_dias_max ?? 0,
  });

  const buscar = useMutation({
    mutationFn: (term: string) => posRpc<VentaDev | null>('pos_venta_para_devolucion', { _q: term }),
    onSuccess: (v) => {
      if (!v) { toast.error('Venta no encontrada — busca por VTA-123, el número solo (123) o la serie 001-101-…'); return; }
      setVenta(v); setCant({}); setMotivo('');
      if (v.es_consumidor_final) toast.warning('Venta a CONSUMIDOR FINAL: no admite nota de crédito');
      else if (!v.factura_autorizada) toast.warning('La factura aún no tiene número — emítela antes de la NC');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // 898: qué preselecciona la empresa cuando la devolución es PARCIAL y la
  // venta se cobró con tarjeta (el reverso no admite monto parcial).
  const { data: politicaParcial = 'efectivo' } = useQuery<string>({
    queryKey: ['empresa-devolucion-parcial-tarjeta', user?.empresa.id],
    enabled: !!user?.empresa.id,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => (await apiClient.get<{ devolucion_parcial_tarjeta: string }[]>(
      `/rest/autenticacion.empresas?id=eq.${user!.empresa.id}&select=devolucion_parcial_tarjeta`))
      .data[0]?.devolucion_parcial_tarjeta ?? 'efectivo',
  });

  // ¿Esta venta se cobró con tarjeta? Decide si el aviso de la parcial aplica.
  // Se mira el rastro que deja cualquiera de las dos vías: el datáfono (el
  // cajero teclea adquirente/autorización) y la pasarela (referencia PM-…).
  const { data: ventaConTarjeta = false } = useQuery<boolean>({
    queryKey: ['venta-con-tarjeta', venta?.venta_id],
    enabled: !!venta?.venta_id,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const r = (await apiClient.get<{ adquirente: string | null; tarjeta_autorizacion: string | null; referencia: string | null }[]>(
        `/rest/pos.venta_pagos?venta_id=eq.${venta!.venta_id}&select=adquirente,tarjeta_autorizacion,referencia`)).data;
      return r.some((p) => p.adquirente || p.tarjeta_autorizacion || (p.referencia ?? '').startsWith('PM-'));
    },
  });

  const saldo = (l: LineaVenta) => Number(l.cantidad) - Number(l.devuelto);
  const totalDev = useMemo(() => {
    if (!venta) return 0;
    return venta.lineas.reduce((a, l) => {
      const c = Number(cant[l.venta_linea_id]) || 0;
      const neto = c * (Number(l.precio_unitario) - Number(l.descuento || 0));
      return a + neto + neto * Number(l.iva_tarifa) / 100;
    }, 0);
  }, [venta, cant]);

  // La devolución a la tarjeta exige devolver TODO lo que queda por devolver:
  // el reverso es total o nada. Si sobra una sola unidad, no se ofrece.
  const esTotal = useMemo(() => {
    if (!venta) return false;
    return venta.lineas.every((l) => (Number(cant[l.venta_linea_id]) || 0) >= saldo(l))
      && venta.lineas.some((l) => (Number(cant[l.venta_linea_id]) || 0) > 0);
  }, [venta, cant]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Si deja de ser total, no puede quedarse en 'tarjeta': se cae a lo que la
  // empresa haya fijado para las parciales con tarjeta (898). Es preselección,
  // no candado — el cajero puede cambiarla ahí mismo.
  useEffect(() => {
    if (reembolso === 'tarjeta' && !esTotal) {
      setReembolso(politicaParcial === 'monedero' ? 'monedero' : 'efectivo');
    }
  }, [esTotal, reembolso, politicaParcial]);

  // Plazo de devolución: días transcurridos desde la fecha de la factura vs. el límite de la empresa.
  const plazo = useMemo(() => {
    if (!venta || !diasMax) return null;
    const base = venta.factura_fecha ?? venta.fecha;
    const dias = Math.floor((Date.now() - new Date(base).getTime()) / 86400000);
    const limite = new Date(new Date(base).getTime() + diasMax * 86400000);
    return { dias, vencida: dias > diasMax, limite: limite.toLocaleDateString(LOCALE) };
  }, [venta, diasMax]);

  // Devolución total: rellena cada línea con su saldo devolvible (sin capturar ítem por ítem).
  const devolverTodo = () => {
    if (!venta) return;
    const next: Record<string, string> = {};
    venta.lineas.forEach((l) => { const s = saldo(l); if (s > 0) next[l.venta_linea_id] = String(s); });
    const n = Object.keys(next).length;
    if (n === 0) { toast.warning('Esta factura no tiene saldo devolvible (ya se devolvió por completo)'); return; }
    setCant(next);
    toast.success(`Devolución total: ${n} ${n === 1 ? 'ítem cargado' : 'ítems cargados'} por su saldo pendiente`);
  };

  const imprimir = (d: DevRow) => imprimirNotaCredito({
    codigo: d.codigo, ncNumero: d.nc_numero, claveAcceso: d.nc_clave_acceso, facturaNumero: d.factura ?? '—',
    fecha: new Date(d.fecha).toLocaleString(LOCALE), caja: d.caja, cajero: d.cajero,
    cliente: d.cliente, identificacion: d.identificacion, motivo: d.motivo, reembolso: d.reembolso,
    lineas: d.lineas.map((l) => ({ descripcion: l.descripcion, cantidad: Number(l.cantidad), precio_unitario: Number(l.precio_unitario), subtotal: Number(l.subtotal) })),
    subtotal: Number(d.subtotal), iva: Number(d.iva), total: Number(d.total),
  } satisfies NotaCreditoImprimible, empresa ?? {}, (formato === 'a4' ? 'a4' : 'rollo80'));

  const registrar = useMutation({
    mutationFn: () => posRpc<{ devolucion_id: string; codigo: string; total: number; nc_numero: string | null }>('pos_devolucion_registrar', {
      payload: {
        caja_id: cajaId, venta_id: venta!.venta_id, motivo, reembolso,
        lineas: venta!.lineas
          .filter((l) => (Number(cant[l.venta_linea_id]) || 0) > 0)
          .map((l) => ({ venta_linea_id: l.venta_linea_id, cantidad: Number(cant[l.venta_linea_id]), reingresa_stock: true })),
      },
    }),
    onSuccess: (r) => {
      toast.success(`Devolución ${r.codigo} registrada · NC ${r.nc_numero ?? 'en emisión'} · ${money(r.total)}`);
      setVenta(null); setQ(''); setCant({});
      void qc.invalidateQueries({ queryKey: ['pos-devoluciones'] });
      // imprime la NC recién emitida
      setTimeout(() => {
        void posRpc<DevRow[]>('pos_devoluciones_listar', { _desde: hoy(), _hasta: hoy() }).then((rows) => {
          const d = rows.find((x) => x.codigo === r.codigo);
          if (d) imprimir(d);
        });
      }, 400);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const listo = venta && !venta.es_consumidor_final && venta.factura_autorizada && motivo.trim() !== '' && totalDev > 0 && !plazo?.vencida;

  // Listado del día
  const { data: devs = [], isFetching } = useQuery<DevRow[]>({
    queryKey: ['pos-devoluciones'],
    queryFn: () => posRpc('pos_devoluciones_listar', { _desde: hoy(), _hasta: hoy() }),
    staleTime: 0, refetchOnMount: 'always',
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><Undo2 className="w-5 h-5" /> Devoluciones de venta (Notas de Crédito)</h1>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-4">
          <Campo label="Caja">
            <select className={`${fieldCls} h-9 w-56`} value={cajaId} onChange={(e) => setCajaId(e.target.value)}>
              {cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Venta o factura (VTA-123 · 123 · 001-101-…)" className="grow max-w-md">
            <input className={`${fieldCls} h-9`} value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && q.trim()) buscar.mutate(q.trim()); }} placeholder="VTA-123, 123 o 001-101-000000456" />
          </Campo>
          <Button isLoading={buscar.isPending} disabled={!q.trim()} onClick={() => buscar.mutate(q.trim())}>
            <Search className="w-4 h-4 mr-1" /> Buscar
          </Button>
          <span className="ml-auto text-xs text-muted-foreground">Impresión del local: <span className="font-medium uppercase">{formato === 'a4' ? 'A4' : 'Rollo 80mm'}</span></span>
        </CardContent>
      </Card>

      {!venta && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            Últimas facturas con saldo devolvible {cargandoRecientes && <Loader2 className="w-4 h-4 animate-spin" />}
            <select className={`${fieldCls} h-8 ml-auto w-72 text-xs`} value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              <option value="">— Todos los clientes —</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.razon_social} ({c.identificacion ?? 's/i'})</option>)}
            </select>
          </CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Venta</th>
                <th className="py-2 pr-3">Factura</th><th className="py-2 pr-3">Cliente</th>
                <th className="py-2 pr-3 text-right">Total</th><th className="py-2 pr-3 text-right">Ya devuelto</th>
              </tr></thead>
              <tbody>
                {recientes.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">
                  {cargandoRecientes ? 'Consultando…' : 'Sin facturas devolvibles (las ventas a CONSUMIDOR FINAL no admiten NC)'}</td></tr>}
                {recientes.map((r, i) => (
                  <tr key={r.venta_id} className="border-b last:border-0 cursor-pointer hover:bg-muted/50"
                      title="Clic para cargar esta factura" onClick={() => { setQ(r.codigo); buscar.mutate(r.codigo); }}>
                    <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{new Date(r.fecha).toLocaleString(LOCALE, { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.codigo}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.factura ?? '(sin número)'}</td>
                    <td className="py-1.5 pr-3 max-w-[16rem] truncate">{r.cliente} <span className="text-xs text-muted-foreground">({r.identificacion ?? 's/i'})</span></td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(r.total)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{Number(r.devuelto) > 0 ? money(r.devuelto) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {venta && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">
            {venta.codigo} · Factura {venta.factura ?? '(sin emitir)'} · {venta.cliente} {venta.identificacion ? `(${venta.identificacion})` : ''}
          </CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {venta.es_consumidor_final && (
              <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300">
                Venta a <b>CONSUMIDOR FINAL</b>: la normativa SRI no permite notas de crédito sin comprador identificado. No es posible procesar esta devolución.
              </div>
            )}
            {!venta.factura_autorizada && !venta.es_consumidor_final && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                La factura de esta venta aún no tiene número de comprobante (pendiente de emisión SRI). Emítela primero desde Facturación.
              </div>
            )}

            {plazo?.vencida && (
              <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">
                <b>Fuera del plazo de devolución.</b> Esta factura tiene {plazo.dias} días; el máximo permitido es {diasMax} días
                (venció el {plazo.limite}). No es posible emitir la nota de crédito.
              </div>
            )}
            {plazo && !plazo.vencida && (
              <p className="text-xs text-muted-foreground">
                Dentro del plazo de devolución: {plazo.dias} de {diasMax} días · vence el {plazo.limite}.
              </p>
            )}

            {!venta.es_consumidor_final && venta.factura_autorizada && !plazo?.vencida && (
              <div className="flex items-center justify-between gap-3 rounded-md border border-dashed bg-muted/30 px-3 py-2">
                <p className="text-xs text-muted-foreground">
                  El stock <b>siempre reingresa</b> al inventario en la devolución. Usa <b>Devolver factura completa</b> para
                  devolver todos los ítems con su saldo pendiente sin capturarlos uno por uno.
                </p>
                <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={devolverTodo}>
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Devolver factura completa
                </Button>
              </div>
            )}

            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Producto</th><th className="py-2 pr-3 text-right">Vendido</th>
                <th className="py-2 pr-3 text-right">Ya devuelto</th><th className="py-2 pr-3 text-right">Saldo</th>
                <th className="py-2 pr-3 text-right">P. unit.</th><th className="py-2 pr-3 text-right w-32">Devolver</th>
              </tr></thead>
              <tbody>
                {venta.lineas.map((l) => (
                  <tr key={l.venta_linea_id} className="border-b last:border-0">
                    <td className="py-2 pr-3">{l.descripcion}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{Number(l.cantidad)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums text-muted-foreground">{Number(l.devuelto)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums font-medium">{saldo(l)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(Number(l.precio_unitario) - Number(l.descuento || 0))}</td>
                    <td className="py-2 pr-3 text-right">
                      <input type="number" min="0" max={saldo(l)} step="1"
                        className={`${fieldCls} h-8 w-24 text-right ml-auto`} value={cant[l.venta_linea_id] ?? ''}
                        placeholder="0" disabled={venta.es_consumidor_final || saldo(l) <= 0}
                        onChange={(e) => setCant((s) => ({ ...s, [l.venta_linea_id]: e.target.value }))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="grid gap-3 md:grid-cols-[1fr_14rem_auto] items-end">
              <Campo label="Motivo de la devolución *">
                <input className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Talla incorrecta, producto defectuoso…" />
              </Campo>
              <Campo label="Reembolso">
                {ventaConTarjeta && !esTotal && (
                  <span className="mb-1 block text-[11px] text-pendiente">
                    Devolución parcial de una venta con tarjeta: el reverso es total o nada.
                    Se devuelve {politicaParcial === 'monedero' ? 'al monedero' : 'en efectivo'} (política de la empresa).
                  </span>
                )}
                <select className={fieldCls} value={reembolso}
                        onChange={(e) => setReembolso(e.target.value as 'efectivo' | 'monedero' | 'tarjeta')}>
                  <option value="efectivo">Efectivo de la caja</option>
                  <option value="monedero">Abono al monedero del cliente</option>
                  <option value="tarjeta" disabled={!esTotal}>
                    A la tarjeta (reverso){!esTotal ? ' — sólo si devuelves todo' : ''}
                  </option>
                </select>
              </Campo>
              <div className="flex items-center gap-3">
                <div className="text-sm">Total a devolver: <span className="text-lg font-bold font-mono tabular-nums">{money(totalDev)}</span></div>
                <Button isLoading={registrar.isPending} disabled={!listo}
                  onClick={async () => {
                    const comoDevuelve = reembolso === 'efectivo' ? 'reembolso en efectivo'
                      : reembolso === 'monedero' ? 'abono al monedero'
                      : 'reverso a la MISMA TARJETA del cliente';
                    if (await dlg.confirmar(`¿Emitir la nota de crédito por ${money(totalDev)}?`, { detalle: `Modo: ${comoDevuelve}.` })) registrar.mutate();
                  }}>
                  Emitir NC y devolver
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              La NC se emite al SRI con su propia secuencia y referencia a la factura. Se pueden emitir varias NC de una misma
              factura mientras no se supere el saldo por línea. El reembolso en efectivo queda como egreso del turno (resta del arqueo).
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">
          Devoluciones de hoy ({devs.length}) {isFetching && <Loader2 className="inline w-4 h-4 animate-spin ml-1" />}
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Devolución</th><th className="py-2 pr-3">NC</th>
              <th className="py-2 pr-3">Factura</th><th className="py-2 pr-3">Cliente</th><th className="py-2 pr-3">Motivo</th>
              <th className="py-2 pr-3">Reembolso</th><th className="py-2 pr-3 text-right">Total</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {devs.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin devoluciones hoy</td></tr>}
              {devs.map((d, i) => (
                <tr key={d.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{d.codigo}</td>
                  <td className="py-2 pr-3 font-mono text-xs" title={d.nc_estado ?? ''}>{d.nc_numero ?? 'en emisión'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{d.factura ?? '—'}</td>
                  <td className="py-2 pr-3">{d.cliente}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{d.motivo}</td>
                  <td className="py-2 pr-3 text-xs capitalize">{d.reembolso}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(d.total)}</td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant="ghost" title="Imprimir NC" onClick={() => imprimir(d)}><Printer className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {devs.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={7}>Totales ({devs.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(devs.reduce((a, d) => a + Number(d.total), 0))}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
